/**
 * SharedBundle (Iter 36.j) — public, unauthenticated read-only viewer
 * for steering-bundle snapshot tokens.
 *
 * Mounts at /share/bundle/:token. Calls
 * ``GET /api/share/bundle/:token`` (no auth) to fetch the composed
 * bundle payload — workspace metadata + brief + churn + radar — and
 * presents a polished read-only summary plus a PDF download button.
 *
 * Anyone with the URL can view; the URL itself IS the secret. Backend
 * collapses unknown / expired / revoked tokens to 404 so we never need
 * to render a "permission denied" surface — every failure looks the
 * same to a leaker.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  StackIcon as Stack,
  FilesIcon as Files,
  TrendUpIcon as TrendUp,
  ArchiveIcon as Archive,
  WarningOctagonIcon as WarningOctagon,
  DownloadSimpleIcon as Download,
  ShieldCheckIcon as ShieldCheck,
  CircleNotchIcon as Spinner,
  ClockIcon as Clock,
  PackageIcon as Package,
} from "@phosphor-icons/react";
import { getBundleSnapshot, bundleSnapshotPdfUrl } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatCard({ label, value, color, testId, icon: Icon }) {
  return (
    <div className="ml-card p-4" data-testid={testId}>
      <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1">
        {Icon && <Icon size={10} weight="bold" />}
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1" style={{ color }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, label, sub }) {
  return (
    <div className="mb-3">
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5">
        <Icon size={11} weight="bold" /> {label}
      </div>
      {sub && (
        <p className="text-xs text-[var(--ml-text-secondary)] mt-0.5">{sub}</p>
      )}
    </div>
  );
}

export default function SharedBundle() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getBundleSnapshot(token)
      .then(setData)
      .catch(() =>
        setError("This snapshot link is invalid, expired, or has been revoked."),
      );
  }, [token]);

  useEffect(() => {
    if (!data) return;
    const ws = data.workspace?.name || "Modernization Lab";
    const prev = document.title;
    document.title = `${ws} · Steering bundle`;
    return () => {
      document.title = prev;
    };
  }, [data]);

  if (error) {
    return (
      <div
        className="max-w-2xl mx-auto px-6 py-16 text-center"
        data-testid="bundle-snapshot-error"
      >
        <WarningOctagon size={48} weight="duotone" className="text-[#a2191f] inline mb-3" />
        <h1 className="text-2xl font-semibold mb-2">Snapshot not available</h1>
        <p className="text-[var(--ml-text-secondary)]">{error}</p>
        <p className="text-xs text-[var(--ml-text-secondary)] mt-4">
          If you received this URL from a Modernization Lab consultant, ask them to mint a fresh snapshot.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="max-w-2xl mx-auto px-6 py-16 text-center"
        data-testid="bundle-snapshot-loading"
      >
        <Spinner size={32} className="animate-spin text-[var(--ml-text-secondary)] inline mb-3" />
        <p className="text-sm text-[var(--ml-text-secondary)]">Composing bundle…</p>
      </div>
    );
  }

  const { workspace, brief, churn, radar, label, expires_at } = data;
  const sap = brief?.sap_packages;
  const ibmi = brief?.ibmi_files;
  const churnTotals = churn?.totals || {};
  const candidates = radar?.candidates || [];

  return (
    <div className="min-h-screen bg-white" data-testid="shared-bundle">
      {/* Brand strip */}
      <div className="bg-[#0f62fe] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="ml-mono text-[10px] uppercase tracking-widest opacity-80">Modernization Lab</div>
          <div className="text-sm font-semibold">Steering bundle</div>
        </div>
        <a
          href={bundleSnapshotPdfUrl(token)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="bundle-snapshot-download-pdf"
          className="text-white hover:bg-white/10 px-3 py-1 text-sm flex items-center gap-1.5 border border-white/20"
        >
          <Download size={14} weight="bold" /> Download PDF
        </a>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        {/* Watermark banner */}
        {(data.recipient_email || data.recipient_label || label) && (
          <div
            className="border-l-4 border-[#0f62fe] bg-[#edf5ff] px-4 py-3 text-sm"
            data-testid="bundle-snapshot-watermark"
          >
            <div className="flex items-center gap-2 text-[#001d6c]">
              <ShieldCheck size={14} weight="bold" />
              <span>
                {label && (
                  <>
                    <strong>{label}</strong>
                    {(data.recipient_email || data.recipient_label) && " · "}
                  </>
                )}
                {(data.recipient_email || data.recipient_label) && (
                  <>Shared with <strong>{data.recipient_email || data.recipient_label}</strong></>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <Package size={11} weight="bold" /> Modernization steering bundle
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">{workspace.name}</h1>
          {workspace.client_name && (
            <p className="text-[var(--ml-text-secondary)]">{workspace.client_name}</p>
          )}
          <p className="text-xs text-[var(--ml-text-secondary)] mt-1 flex items-center gap-1">
            <Clock size={11} weight="bold" /> Link expires {fmtDate(expires_at)}
          </p>
        </div>

        {/* Top stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bundle-snapshot-totals">
          <StatCard
            label="SAP packages"
            value={sap?.totals?.packages ?? 0}
            icon={Stack}
            testId="bundle-snapshot-stat-packages"
          />
          <StatCard
            label="IBM-i files"
            value={ibmi?.totals?.files ?? 0}
            icon={Files}
            testId="bundle-snapshot-stat-files"
          />
          <StatCard
            label="Journal writes"
            value={churnTotals.total_entries ?? 0}
            icon={TrendUp}
            testId="bundle-snapshot-stat-writes"
          />
          <StatCard
            label="Dead candidates"
            value={candidates.length}
            color={candidates.length > 0 ? "#b28600" : undefined}
            icon={Archive}
            testId="bundle-snapshot-stat-candidates"
          />
        </div>

        {/* Empty state */}
        {!brief?.has_signal && churnTotals.total_entries === 0 && candidates.length === 0 && (
          <div
            className="ml-card p-6 text-sm text-[var(--ml-text-secondary)]"
            data-testid="bundle-snapshot-empty"
          >
            This workspace has no SAP, IBM-i, or journal data ingested yet. Once the
            consultant imports the catalog and journal slice, this snapshot will
            populate automatically — no need to mint a new URL.
          </div>
        )}

        {/* SAP top packages */}
        {sap?.top?.length > 0 && (
          <div className="ml-card p-5" data-testid="bundle-snapshot-sap-section">
            <SectionHeader
              icon={Stack}
              label="Top SAP packages by transition risk"
              sub="Heat-map ranked — modified-standard packages first."
            />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/40">
                  <tr>
                    <th className="px-3 py-2 text-left">Package</th>
                    <th className="px-3 py-2 text-left">Band</th>
                    <th className="px-3 py-2 text-right">Score</th>
                    <th className="px-3 py-2 text-right">Objects</th>
                    <th className="px-3 py-2 text-right">Mod-std</th>
                    <th className="px-3 py-2 text-right">LOC</th>
                  </tr>
                </thead>
                <tbody>
                  {sap.top.map((p) => (
                    <tr
                      key={p.package}
                      className="border-b border-[var(--ml-border)]"
                      data-testid={`bundle-snapshot-sap-row-${p.package.replace(/[/\s()]/g, "_")}`}
                    >
                      <td className="px-3 py-2 ml-mono font-semibold">{p.package}</td>
                      <td className="px-3 py-2 ml-mono text-xs">{p.risk_band}</td>
                      <td className="px-3 py-2 ml-mono text-right">{p.risk_score.toLocaleString()}</td>
                      <td className="px-3 py-2 ml-mono text-right">{p.object_count}</td>
                      <td className="px-3 py-2 ml-mono text-right">
                        {p.modified_standard_count > 0 ? (
                          <span className="text-[#a2191f] font-semibold">{p.modified_standard_count}</span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 ml-mono text-right">{p.total_loc.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* IBM-i top files */}
        {ibmi?.top_files?.length > 0 && (
          <div className="ml-card p-5" data-testid="bundle-snapshot-ibmi-section">
            <SectionHeader
              icon={Files}
              label="IBM-i files anchored in business logic"
              sub="Ranked by rules × programs — high blast radius if renamed."
            />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/40">
                  <tr>
                    <th className="px-3 py-2 text-left">File</th>
                    <th className="px-3 py-2 text-right">Rules</th>
                    <th className="px-3 py-2 text-right">Programs</th>
                    <th className="px-3 py-2 text-right">Fields</th>
                    <th className="px-3 py-2 text-right">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {ibmi.top_files.map((f) => (
                    <tr
                      key={f.file}
                      className="border-b border-[var(--ml-border)]"
                      data-testid={`bundle-snapshot-ibmi-row-${f.file.replace(/[/\s()]/g, "_")}`}
                    >
                      <td className="px-3 py-2 ml-mono font-semibold">{f.file}</td>
                      <td className="px-3 py-2 ml-mono text-right">{f.rule_count}</td>
                      <td className="px-3 py-2 ml-mono text-right">{f.program_count}</td>
                      <td className="px-3 py-2 ml-mono text-right">{f.field_count}</td>
                      <td className="px-3 py-2 ml-mono text-right font-semibold">{f.weight}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top write churn */}
        {churn?.top_files?.length > 0 && (
          <div className="ml-card p-5" data-testid="bundle-snapshot-churn-section">
            <SectionHeader
              icon={TrendUp}
              label="Top write-churn files"
              sub={`Imported journal slice: ${churnTotals.total_entries?.toLocaleString() ?? 0} write entries.`}
            />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/40">
                  <tr>
                    <th className="px-3 py-2 text-left">Library</th>
                    <th className="px-3 py-2 text-left">File</th>
                    <th className="px-3 py-2 text-right">Writes</th>
                    <th className="px-3 py-2 text-right">Ins / Upd / Del</th>
                  </tr>
                </thead>
                <tbody>
                  {churn.top_files.slice(0, 10).map((f) => (
                    <tr
                      key={`${f.library_name}.${f.file_name}`}
                      className="border-b border-[var(--ml-border)]"
                    >
                      <td className="px-3 py-2 ml-mono">{f.library_name}</td>
                      <td className="px-3 py-2 ml-mono font-semibold">{f.file_name}</td>
                      <td className="px-3 py-2 ml-mono text-right">{(f.total ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-2 ml-mono text-right text-xs text-[var(--ml-text-secondary)]">
                        {f.ins ?? 0} / {f.upd ?? 0} / {f.del ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Decommission radar */}
        {candidates.length > 0 && (
          <div
            className="ml-card p-5 border-l-4 border-[#f1c21b]"
            data-testid="bundle-snapshot-radar-section"
          >
            <SectionHeader
              icon={Archive}
              label="Decommission candidates"
              sub="Referenced by programs but zero writes in the imported journal window."
            />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/40">
                  <tr>
                    <th className="px-3 py-2 text-left">Library</th>
                    <th className="px-3 py-2 text-left">File</th>
                    <th className="px-3 py-2 text-right">Programs</th>
                    <th className="px-3 py-2 text-right">Refs</th>
                    <th className="px-3 py-2 text-right">Rows</th>
                    <th className="px-3 py-2 text-left">Journaled</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.slice(0, 25).map((c) => (
                    <tr
                      key={`${c.library_name}.${c.file_name}`}
                      className="border-b border-[var(--ml-border)]"
                    >
                      <td className="px-3 py-2 ml-mono">{c.library_name}</td>
                      <td className="px-3 py-2 ml-mono font-semibold">{c.file_name}</td>
                      <td className="px-3 py-2 ml-mono text-right">{c.program_count}</td>
                      <td className="px-3 py-2 ml-mono text-right">{c.ref_count}</td>
                      <td className="px-3 py-2 ml-mono text-right">{(c.record_count ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-2 ml-mono text-xs">{c.journaled ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-xs text-[var(--ml-text-secondary)] border-t border-[var(--ml-border)] pt-4 mt-8">
          This is a read-only snapshot link issued by a Modernization Lab consultant.
          The data shown is rebuilt server-side from the customer&apos;s catalog and
          journal extracts every time the link is opened — what you see is the
          current state, not a frozen point-in-time export.
        </div>
      </div>
    </div>
  );
}
